<template>
  <div class="col-sm-12 list-container nopads">
    <div v-if="tableItems && tableItems.length > 0" class="col-sm-12 list-content result-table">
      <Transition name="fade">
          <vue3-datatable
              ref="resultTable"
              :rows="tableItems"
              :columns="fields"
              :pagination="false"
              :sticky-header="true"
              height="60vh"
              :sortable="true"
              skin="bh-table-compact"
              :row-class="getRowClass"
              cell-class="routa-table-cell"
              @row-click="showDetails"
          />
      </Transition>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import {restApi} from '../mixins/RestApiMixin'
import {downloadHelper} from '../mixins/DownloadMixin'
import {workShift} from '../mixins/WorkShift'
import Vue3Datatable from "@bhplugin/vue3-datatable";
import {inject} from "vue";

export default {
    name: 'WorkdayDetailList',
    components: {Vue3Datatable},
    mixins: [timeUtils, restApi, downloadHelper, workShift],
    props: {
        results: {
            type: Object,
            default() {
                return null
            }
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            tableItems: [],
            fields: [],
            NOT_APPROVED: 'NOK'
        }
    },
    mounted: function () {
        this.initFields()
        this.initTableItems()
    },
    methods: {
        getRowClass(row) {
            return row.workTimeApproval && row.workTimeApproval === 'OK' ? 'routa-table-row selected-row' : 'routa-table-row'
        },

        initFields: function () {
            this.fields = [
                { field: 'date', title: this.$t('area_report.created'), sort: false, type: 'date', headerClass: 'routa-table-header'},
                { field: 'weekDay', title: this.$t('work_time.weekday'), sort: false, headerClass: 'routa-table-header'},
                { field: 'person', title: this.$t('work_time.person'), sort: false, headerClass: 'routa-table-header'},
                { field: 'start_time', title: this.$t('work_time.start'), sort: false, type: 'date', headerClass: 'routa-table-header'},
                { field: 'end_time', title: this.$t('work_time.end'), sort: false, type: 'date', headerClass: 'routa-table-header'},
                { field: 'shift', title: this.$t('work_shift.shift'), sort: false, headerClass: 'routa-table-header'},
                { field: 'workTime', title: this.$t('work_time.work_time'), sort: false, type: 'number', headerClass: 'routa-table-header'},
                { field: 'cumulativeWorkTime', title: this.$t('work_time.work_time_cumulative'), sort: false, type: 'number', headerClass: 'routa-table-header'},
                { field: 'cumulativeWeekWorkTime', title: this.$t('work_time.work_time_week_cumulative'), sort: false, type: 'number', headerClass: 'routa-table-header'},
                { field: 'cumulativeMonthWorkTime', title: this.$t('work_time.work_time_month_cumulative'), sort: false, type: 'number', headerClass: 'routa-table-header'},
                { field: 'normalWorkTime', title: this.$t('work_time.normal_work_time'), sort: true, type: 'number', headerClass: 'routa-table-header'},
                { field: 'overTime50WorkTime', title: this.$t('work_time.over_time_50'), sort: true, type: 'number', headerClass: 'routa-table-header'},
                { field: 'overTime100WorkTime', title: this.$t('work_time.over_time_100'), sort: true, type: 'number', headerClass: 'routa-table-header'},
                { field: 'workTimeApproval', title: this.$t('work_time.approval'), sort: true, headerClass: 'routa-table-header'}
            ]

            if (this.results.modes) {
                let modes = this.results.modes;
                modes.forEach(function (mode) {
                    this.fields.push({
                        field: mode,
                        title: mode,
                        sort: false,
                        headerClass: 'routa-table-header'
                    })
                }, this);
            }

            this.fields.push(
                {
                    field: 'dailyAllowance',
                    title: this.$t('daily_allowance.full'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'partialDailyAllowance',
                    title: this.$t('daily_allowance.partial'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'dailyAllowanceApproval',
                    title: this.$t('daily_allowance.approval'),
                    sort: true,
                    headerClass: 'routa-table-header'
                },
                {
                    field: 'info',
                    title: this.$t('work_time.note'),
                    sort: true,
                    headerClass: 'routa-table-header'
                }
            );
        },

        initTableItems: function () {
            this.tableItems = []
            if (this.results && this.results.records) {
                this.results.records.forEach(function (record, index) {
                    let hasValidData = record.mode_hours.length !== 0
                    var row = {
                        date: this.getShortDateString(record.start_time),
                        weekDay: this.getWeekDay(record.start_time),
                        person: record.last_name + ', ' + record.first_name,
                        start: hasValidData ? this.toLocalTime(record.start_time) : '',
                        end: (hasValidData && record.end_time) ? this.toLocalTime(record.end_time) : '',
                        shift: this.getShiftsStr(record.shifts),
                        workTime: this.getWorkTime(record.work_time),
                        cumulativeWorkTime: this.getWorkTime(record.cumulative_work_time),
                        cumulativeWeekWorkTime: this.getWorkTime(record.cumulative_week_work_time),
                        cumulativeMonthWorkTime: this.getWorkTime(record.cumulative_month_work_time),
                        normalWorkTime: this.getWorkTime(record.normal_work_time),
                        overTime50WorkTime: this.getWorkTime(record.over_time_50),
                        overTime100WorkTime: this.getWorkTime(record.over_time_100),
                        workTimeApproval: record.work_time_approval,
                        index: index
                    }
                    if (this.results.modes && record.mode_hours) {
                        for (var i = 0; i < this.results.modes.length; i++) {
                            var key = this.results.modes[i]
                            let value = record.mode_hours[key]
                            row[key] = this.getWorkTime(value)
                        }
                    }
                    row['dailyAllowance'] = record.daily_allowance
                    row['partialDailyAllowance'] = record.partial_daily_allowance
                    row['dailyAllowanceApproval'] = record.daily_allowance_approval
                    row['info'] = record.info
                    // Highlight unapproved working hours and daily allowances
                    if ((record.work_time_approval && record.work_time_approval === this.NOT_APPROVED) ||
                        (record.daily_allowance_approval && record.daily_allowance_approval === this.NOT_APPROVED)) {
                        row['_rowVariant'] = 'danger'
                    }

                    this.tableItems.push(row)
                }, this)
            }
        },

        showDetails: function (details) {
            this.$emit('showDetails', this.results.records[details.index])
        },

        exportExcel: function (params) {
            this.loading = true
            this.restPostDownloadFile(this.workDayDetailsReportUrl, this.getCommonReportParams(params), this.success, this.fail)
        },
        success: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('work_time.work_day_report_file_name'))
        },
        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },
        getShiftsStr: function (shifts) {
            let result = ''
            if (shifts) {
                for (let i = 1; i <= 4; i++) {
                    if (shifts[i]) {
                        if (result.length > 0) {
                            result += '\n'
                        }
                        result += this.getWorkShiftName(i)
                    }
                }
            }
            return result
        }
    }
}
</script>
